import * as React from 'react'
import { BarChart } from '@mui/x-charts/BarChart'
import { axisClasses } from '@mui/x-charts/ChartsAxis'
import { useTheme } from '@mui/material/styles'
import { getMonths } from '../../../../utils/helper'
import { Box, Skeleton, Paper, Typography, Divider } from '@mui/material'

type Props = {
  data: any
  title: string
  className?: string
  fromDate?: any
  toDate?: any
  height?: any
  xLabelsProp?: any
  stackKeys?: string[]
  stackColors?: string[]
  stackLabels?: string[]
  hideLabel?: boolean
  showTotalInTooltip?: boolean
  tooltipOrder?: string[]
}

const CustomAxisTooltip = (props: any) => {
  const { axisValue, series, dataIndex, order } = props
  
  if (dataIndex == null || !series) {
    return null
  }

  const seriesItems = series.map((s: any) => {
    const value = s.data[dataIndex]
    return {
      label: s.label,
      value: value,
      color: s.getColor ? s.getColor(dataIndex) : s.color,
      formattedValue: s.valueFormatter ? s.valueFormatter(value, { dataIndex }) : value
    }
  }).filter((item: any) => item.value !== null && item.value !== undefined)

  if (order && order.length > 0) {
    seriesItems.sort((a: any, b: any) => {
      const indexA = order.indexOf(a.label);
      const indexB = order.indexOf(b.label);
      // If both are in order, sort by index
      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      // If a is in order, it comes first
      if (indexA !== -1) return -1;
      // If b is in order, it comes first
      if (indexB !== -1) return 1;
      // preserve original order
      return 0;
    });
  }

  const total = seriesItems.reduce((acc: number, item: any) => acc + (Number(item.value) || 0), 0)

  return (
    <Paper elevation={3} sx={{ p: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>{axisValue}</Typography>
      {seriesItems.map((item: any, index: number) => (
        <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
          <Box sx={{ width: 10, height: 10, backgroundColor: item.color, mr: 1, borderRadius: '50%' }} />
          <Typography variant="body2" sx={{ mr: 1 }}>
            {item.label}:
          </Typography>
          <Typography variant="body2" fontWeight="bold">
            {item.formattedValue}
          </Typography>
        </Box>
      ))}
      <Divider sx={{ my: 1 }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" fontWeight="bold">Total:</Typography>
        <Typography variant="body2" fontWeight="bold">{total}</Typography>
      </Box>
    </Paper>
  )
}

export default function CustomBarChart(props: Props) {
  const { className, data, title, fromDate, toDate, height, xLabelsProp, stackKeys, stackColors, stackLabels, hideLabel, showTotalInTooltip, tooltipOrder } = props
  const theme = useTheme()

  const xLabels = xLabelsProp || getMonths(fromDate, toDate)?.map((mon: any) => mon?.month)

  // Ensure data and xLabels are properly aligned and contain valid values
  const processedData = React.useMemo(() => {
    if (!Array.isArray(data) || !Array.isArray(xLabels)) {
      return { chartData: [], labels: [] }
    }

    // Ensure data and labels have the same length
    const minLength = Math.min(data.length, xLabels.length)
    const labels = xLabels.slice(0, minLength)
    let chartData = data.slice(0, minLength)

    if (!stackKeys) {
      // Filter out null/undefined values and ensure numeric data for simple bar chart
      chartData = chartData.map((value: any) => {
        const numValue = Number(value)
        return isNaN(numValue) ? 0 : numValue
      })
    }

    return { chartData, labels }
  }, [data, xLabels, stackKeys])

  const chartSetting = React.useMemo(() => {
    return {
      height: height || 362,
      sx: {
        [`.${axisClasses.left} .${axisClasses.label}`]: {
          transform: 'none', // Ensure no unwanted transformations
        },
        // Add other style overrides as necessary
        '.MuiBarChart-bar': {
          stroke: 'none', // Remove any stroke that might be causing the line
        },
      },
    }
  }, [height])

  const TooltipContent = React.useCallback((tooltipProps: any) => (
    <CustomAxisTooltip {...tooltipProps} order={tooltipOrder} />
  ), [tooltipOrder])

  // Don't render chart if we don't have valid data
  if (!processedData.chartData.length || !processedData.labels.length) {
    return (
      <Box sx={{ height: height || 362 }} className="flex items-center justify-center">
        <div>No Data To Display</div>
      </Box>
    )
  }

  const getSeries = () => {
    if (stackKeys && stackKeys.length > 0) {
      return stackKeys.map((key, index) => ({
        data: processedData.chartData.map((d: any) => d[key] || 0),
        label: stackLabels?.[index] || key,
        stack: 'total',
        color: stackColors?.[index] || (index === 0 ? theme.palette.primary.main : theme.palette.secondary.main)
      }))
    }
    return [
      {
        data: processedData.chartData,
        label: title,
        type: 'bar',
        color: theme.palette.primary.light,
      },
    ]
  }

  return (
    <React.Fragment>
      {data === null ? (
        <Box sx={{ height: height || 362 }} className="flex items-end justify-around relative pb-12 pl-6">
          <Skeleton variant="rectangular" width={50} height={250} />
          <div className="hidden xl:block">
            <Skeleton variant="rectangular" width={50} height={200} />
          </div>
          <div className="hidden xl:block">
            <Skeleton variant="rectangular" width={50} height={180} />
          </div>
          <Skeleton variant="rectangular" width={50} height={18} />
          <div className="hidden lg:block">
            <Skeleton variant="rectangular" width={50} height={108} />
          </div>
          <Skeleton variant="rectangular" width={50} height={220} />
        </Box>
      ) : (
        <BarChart
          borderRadius={8}
          className={`${className} capitalize`}
          series={getSeries() as any}
          xAxis={[
            {
              scaleType: 'band',
              data: processedData.labels,
            },
          ]}
          yAxis={[
            {
              valueFormatter: (value: number) => Math.round(value).toString(),
              tickLabelInterval: (value: number) => Number.isInteger(value),
            },
          ]}
          slotProps={{ legend: { hidden: hideLabel } }}
          tooltip={showTotalInTooltip ? { trigger: 'axis' } : undefined}
          slots={showTotalInTooltip ? { axisContent: TooltipContent } : {}}
          {...chartSetting}
        />
      )}
    </React.Fragment>
  )
}
